import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { FolderPlus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { projectService } from "../../api/services/projectService";
import { Loader, EmptyState } from "../../components/Common";
import CreateProjectDialog from "../../components/CreateProjectDialog";
import { resolveDefaultProject, rememberProject, projectViewPath } from "../../utils/ticketProjectNav";

/**
 * Landing point for the bare /tickets route. Everything under Tickets is
 * now scoped to a Project, so this resolves which project to show (last
 * one the user had selected, else the first) and redirects to its board --
 * or, if no projects exist yet, prompts to create the first one.
 */
export default function TicketsHome() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: projectService.list });

  useEffect(() => {
    if (!projects || projects.length === 0) return;
    const target = resolveDefaultProject(projects);
    rememberProject(target.id);
    navigate(projectViewPath(target.id, "board"), { replace: true });
  }, [projects, navigate]);

  if (isLoading) return <Loader />;

  if (projects && projects.length === 0) {
    return (
      <Box>
        <EmptyState
          title="No projects yet"
          subtitle="Tickets are organized under a Project. Create one to get a Board, List, and Backlog with Phases."
          action={
            <Button variant="contained" startIcon={<FolderPlus size={16} />} onClick={() => setCreateOpen(true)}>
              New Project
            </Button>
          }
        />
        <CreateProjectDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(project) => {
            rememberProject(project.id);
            navigate(projectViewPath(project.id, "board"), { replace: true });
          }}
        />
      </Box>
    );
  }

  return <Loader />;
}
